import type { ChannelDestination, OrganizationDestination } from '../types';
import { isEmptyValue } from './publicationDestinations';

const stringIncludesIgnoreCase = (haystack: unknown, needle: string): boolean => {
    if (isEmptyValue(haystack) || typeof haystack === 'object') {
        return false;
    }
    return String(haystack).toLowerCase().includes(needle);
};

const objectMatchesFilter = (value: unknown, needle: string): boolean => {
    if (isEmptyValue(value)) {
        return false;
    }
    if (typeof value !== 'object') {
        return stringIncludesIgnoreCase(value, needle);
    }
    return Object.values(value as Record<string, unknown>).some((entry) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            return false;
        }
        return stringIncludesIgnoreCase(entry, needle);
    });
};

const filterOrganizationsByText = (
    organizations: OrganizationDestination[],
    channelFilter: string,
): OrganizationDestination[] => {
    const needle = channelFilter.trim().toLowerCase();
    if (!needle) {
        return organizations;
    }
    return organizations.filter((organization) => objectMatchesFilter(organization, needle));
};

const filterChannelsByText = (
    communicationChannels: ChannelDestination[],
    channelFilter: string,
): ChannelDestination[] => {
    const needle = channelFilter.trim().toLowerCase();
    if (!needle) {
        return communicationChannels;
    }
    return communicationChannels.filter((channel) => objectMatchesFilter(channel, needle));
};

const isChannelDestination = (
    destination: OrganizationDestination | ChannelDestination,
): destination is ChannelDestination => {
    return 'id' in destination && (destination as ChannelDestination).id !== undefined;
};

const destinationIdentity = (
    destination: OrganizationDestination | ChannelDestination,
): string => {
    if (isChannelDestination(destination)) {
        return `ch:${destination.id}:${destination.subDestinationKey ?? ''}`;
    }
    return `org:${destination.organizationId}`;
};

const isSameDestination = (
    left: OrganizationDestination | ChannelDestination,
    right: OrganizationDestination | ChannelDestination,
): boolean => destinationIdentity(left) === destinationIdentity(right);

const isInList = (
    destination: OrganizationDestination | ChannelDestination,
    list: Array<OrganizationDestination | ChannelDestination>,
): boolean => list.some((entry) => isSameDestination(entry, destination));

const selectAllRecipients = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
    filteredOrganizations: OrganizationDestination[],
    filteredChannels: ChannelDestination[],
    isSelected: boolean,
): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    const filtered = [...filteredOrganizations, ...filteredChannels];
    return {
        organizations: organizations.map((organization) => ({
            ...organization,
            isSelected: isInList(organization, filtered) ? isSelected : false,
        })),
        communicationChannels: communicationChannels.map((channel) => ({
            ...channel,
            isSelected: isInList(channel, filtered) ? isSelected : false,
        })),
    };
};

const everyFilteredSelected = (
    filtered: Array<OrganizationDestination | ChannelDestination>,
): boolean => {
    if (filtered.length === 0) {
        return true;
    }
    return filtered.every((recipient) => recipient.isSelected);
};

const selectionUpdated = ({
    organizations,
    communicationChannels,
    filteredOrganizations,
    filteredChannels,
    previousAllRecipientsSelected,
    searchTextChanged,
}: {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
    filteredOrganizations: OrganizationDestination[];
    filteredChannels: ChannelDestination[];
    previousAllRecipientsSelected: boolean;
    searchTextChanged?: boolean;
}): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
    allRecipientsSelected: boolean;
} => {
    const filtered = [...filteredOrganizations, ...filteredChannels];
    const allRecipientsSelected = everyFilteredSelected(filtered);

    if (!searchTextChanged && allRecipientsSelected !== previousAllRecipientsSelected) {
        return {
            organizations: organizations.map((organization) => ({
                ...organization,
                isSelected: isInList(organization, filtered) ? organization.isSelected : false,
            })),
            communicationChannels: communicationChannels.map((channel) => ({
                ...channel,
                isSelected: isInList(channel, filtered) ? channel.isSelected : false,
            })),
            allRecipientsSelected,
        };
    }

    return {
        organizations,
        communicationChannels,
        allRecipientsSelected,
    };
};

const toggleDestinationSelection = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
    target: OrganizationDestination | ChannelDestination,
): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    return {
        organizations: organizations.map((organization) => (
            isSameDestination(organization, target)
                ? { ...organization, isSelected: !organization.isSelected }
                : organization
        )),
        communicationChannels: communicationChannels.map((channel) => (
            isSameDestination(channel, target)
                ? { ...channel, isSelected: !channel.isSelected }
                : channel
        )),
    };
};

const setDestinationSelection = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
    target: OrganizationDestination | ChannelDestination,
    isSelected: boolean,
): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    return {
        organizations: organizations.map((organization) => (
            isSameDestination(organization, target)
                ? { ...organization, isSelected }
                : organization
        )),
        communicationChannels: communicationChannels.map((channel) => (
            isSameDestination(channel, target)
                ? { ...channel, isSelected }
                : channel
        )),
    };
};

export {
    filterChannelsByText,
    filterOrganizationsByText,
    selectAllRecipients,
    selectionUpdated,
    setDestinationSelection,
    toggleDestinationSelection,
};
